import { Alert, AlertIcon, Box, Button, HStack, Stack, Switch, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePushSubscription } from './usePushSubscription';

/**
 * User-facing toggle for Web Push notifications. Renders as a card on
 * the Credits page for now; when we add a proper Profile/Settings page
 * this component moves there. Silently hides itself on browsers that
 * don't support Push (older Safari, iOS not-installed-to-home-screen)
 * so we don't dangle a broken toggle.
 */
export function PushToggle() {
  const { t } = useTranslation();
  const { supported, permission, subscribed, busy, error, subscribe, unsubscribe } = usePushSubscription();

  if (!supported) return null;

  const blocked = permission === 'denied';

  return (
    <Box
      bg="rgba(20, 14, 36, 0.55)"
      backdropFilter="blur(14px)"
      border="1px solid"
      borderColor="whiteAlpha.150"
      borderRadius="18px"
      p={{ base: 4, md: 5 }}
      mb={6}
    >
      <HStack justify="space-between" align="flex-start" wrap="wrap" gap={4}>
        <Stack spacing={1} flex="1" minW="240px">
          <Text
            fontSize="11px"
            fontWeight={700}
            color="whiteAlpha.700"
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            {t('push.label', 'Notifications')}
          </Text>
          <Text color="whiteAlpha.900" fontWeight={700}>
            {t('push.title', 'Ping me when a song is ready')}
          </Text>
          <Text fontSize="sm" color="whiteAlpha.600">
            {blocked
              ? t('push.blocked', 'Notifications are blocked in your browser settings for this site. Enable them in the address bar (padlock icon) → Permissions.')
              : subscribed
                ? t('push.subscribed', "You're subscribed on this device. Turn off to stop.")
                : t('push.pitch', 'Get a notification the moment your song finishes generating — no need to keep the tab open.')}
          </Text>
        </Stack>
        <Switch
          isChecked={subscribed}
          isDisabled={busy || blocked}
          colorScheme="pink"
          size="lg"
          onChange={(e) => {
            if (e.target.checked) subscribe();
            else unsubscribe();
          }}
          aria-label={t('push.toggle', 'Toggle notifications')}
        />
      </HStack>
      {error && (
        <Alert
          status="error"
          mt={3}
          borderRadius="10px"
          bg="rgba(60, 20, 30, 0.5)"
          color="whiteAlpha.900"
          border="1px solid"
          borderColor="red.500"
          fontSize="sm"
        >
          <AlertIcon color="red.300" />
          {error}
        </Alert>
      )}
      {blocked && (
        <Button
          mt={3}
          size="sm"
          variant="outline"
          borderColor="whiteAlpha.300"
          color="whiteAlpha.800"
          onClick={() => window.open('chrome://settings/content/notifications', '_blank')}
        >
          {t('push.openSettings', 'Open browser notification settings')}
        </Button>
      )}
    </Box>
  );
}
